/** Download a text blob (fallback when File System Access API is unavailable). */
export function downloadTextFile(
  filename: string,
  content: string,
  mime = "application/rtf",
): void {
  downloadBlob(
    new Blob([content], { type: `${mime};charset=utf-8` }),
    filename,
  );
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 4_000);
}

type FilePickerWindow = Window & {
  showSaveFilePicker?: (options?: {
    suggestedName?: string;
    types?: {
      description: string;
      accept: Record<string, string[]>;
    }[];
  }) => Promise<FileSystemFileHandle>;
};

export function canUseFileSystemAccess(): boolean {
  return typeof (window as FilePickerWindow).showSaveFilePicker === "function";
}

/**
 * Ask for a filename, then download. Does not use File System Access API, so
 * the browser will not warn that the site can see the file.
 * Returns false if the user cancels.
 */
export function saveExportDownload(
  suggestedFilename: string,
  data: string | Blob,
  mime: string,
  extension: string,
): boolean {
  const name = promptDownloadFilename(suggestedFilename, extension);
  if (!name) return false;
  if (typeof data === "string") downloadTextFile(name, data, mime);
  else downloadBlob(data, name);
  return true;
}

/** Let the user edit the suggested name. Null = cancel. */
export function promptDownloadFilename(
  suggested: string,
  extension: string,
): string | null {
  const ext = extension.startsWith(".") ? extension : `.${extension}`;
  const raw = window.prompt("Gem som — filnavn", suggested);
  if (raw === null) return null;
  let name = raw.trim() || suggested;
  name = name.replace(/[\\/:*?"<>|]+/g, "-");
  if (!name.toLowerCase().endsWith(ext.toLowerCase())) {
    name = `${name.replace(/\.[^.]+$/, "")}${ext}`;
  }
  return name;
}

/** One-shot PDF export: name prompt + download (no site file access). */
export async function pickAndWritePdfFile(
  filename: string,
  blob: Blob,
): Promise<FileSystemFileHandle | null> {
  saveExportDownload(filename, blob, "application/pdf", ".pdf");
  return null;
}

/** One-shot Word export: name prompt + download. */
export async function pickAndWriteDocxFile(
  filename: string,
  blob: Blob,
): Promise<FileSystemFileHandle | null> {
  saveExportDownload(
    filename,
    blob,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".docx",
  );
  return null;
}

/** One-shot RTF export: name prompt + download. */
export async function pickAndWriteRtfFile(
  filename: string,
  content: string,
): Promise<FileSystemFileHandle | null> {
  saveExportDownload(filename, content, "application/rtf", ".rtf");
  return null;
}

/**
 * Pick a file the app may keep writing to (løbende RTF). Uses File System
 * Access API — the browser warns that the site can update the file, which is
 * intentional for continuous export only.
 */
export async function pickPersistentRtfFile(
  filename: string,
  content: string,
): Promise<FileSystemFileHandle | null> {
  const w = window as FilePickerWindow;
  if (!w.showSaveFilePicker) {
    saveExportDownload(filename, content, "application/rtf", ".rtf");
    return null;
  }
  try {
    const handle = await w.showSaveFilePicker({
      suggestedName: filename,
      types: [
        {
          description: "Rich Text Format",
          accept: { "application/rtf": [".rtf"] },
        },
      ],
    });
    await writeToFileHandle(handle, content);
    return handle;
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") return null;
    saveExportDownload(filename, content, "application/rtf", ".rtf");
    return null;
  }
}

export async function writeToFileHandle(
  handle: FileSystemFileHandle,
  content: string | Blob,
): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(content);
  await writable.close();
}
